import React, { useState } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Send, Loader2, Check } from 'lucide-react';
import { CURRENT_PRIVACY_POLICY_VERSION } from '@/utils/privacyPolicyVersion';
import { scrollToFirstError } from '@/utils/scrollToFirstError';

const BrandContact = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState([]);

  // UAE PDPL + GDPR consent (split: notice acknowledgement vs. explicit consent)
  const [noticeAcknowledged, setNoticeAcknowledged] = useState(false);
  const [explicitConsent, setExplicitConsent] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    message: ''
  });

  const t = {
    title: 'Work With Us',
    name: 'Your Name',
    company: 'Company Name',
    email: 'Email',
    phone: 'Phone',
    message: 'Your Message',
    messagePlaceholder: 'Share your project details and goals with us...',
    submit: 'Send Inquiry',
    submitting: 'Sending...',
    errorMessage: 'An error occurred. Please try again.',
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Lookup helper for inline field-level error messages
  // (mirrors InfluencerForm pattern; keeps formErrors[] shape intact)
  const errorFor = (fieldName) => {
    const match = formErrors.find((e) => e.field === fieldName);
    return match ? match.message : null;
  };
  const fieldErrorCls = 'mt-1 text-xs text-red-400';

  // Smart phone input: keep only digits + leading "+", strip "00" prefix
  const handlePhoneChange = (e) => {
    let raw = e.target.value || '';
    raw = raw.replace(/[^\d+]/g, '');
    if (raw.startsWith('00')) {
      raw = '+' + raw.substring(2);
    }
    setFormData(prev => ({ ...prev, phone: raw }));
  };

  const validate = () => {
    const errors = [];
    if (!formData.name || !formData.name.trim()) {
      errors.push({ field: 'name', message: 'Name is required' });
    }
    if (!formData.company || !formData.company.trim()) {
      errors.push({ field: 'company', message: 'Company name is required' });
    }
    if (!formData.email || !formData.email.trim()) {
      errors.push({ field: 'email', message: 'Email is required' });
    }
    const phoneDigits = (formData.phone || '').replace(/\D/g, '');
    if (!phoneDigits) {
      errors.push({ field: 'phone', message: 'Phone number is required' });
    } else if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      errors.push({
        field: 'phone',
        message: 'Phone number length looks unusual. Please verify it.',
      });
    }
    if (!formData.message || !formData.message.trim()) {
      errors.push({ field: 'message', message: 'Message is required' });
    }
    if (!noticeAcknowledged) {
      errors.push({ field: 'notice_acknowledged', message: 'Please acknowledge the Privacy Policy' });
    }
    if (!explicitConsent) {
      errors.push({ field: 'explicit_consent', message: 'Please provide explicit consent for data processing' });
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    const errors = validate();
    if (errors.length > 0) {
      setFormErrors(errors);
      scrollToFirstError(errors);
      return;
    }

    setFormErrors([]);
    setLoading(true);

    try {
      await axios.post(`${API}/brand-contact`, {
        ...formData,
        notice_acknowledged: true,
        explicit_consent: true,
        consent_text_version: CURRENT_PRIVACY_POLICY_VERSION,
        consent_timestamp_client: new Date().toISOString(),
      });
      setSuccess(true);
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        message: ''
      });
      setNoticeAcknowledged(false);
      setExplicitConsent(false);

      // Auto-hide success modal after 4 seconds (aligned with InfluencerForm)
      setTimeout(() => {
        setSuccess(false);
      }, 4000);
    } catch (err) {
      setError(t.errorMessage);
      console.error('Form submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-12 bg-black bg-opacity-75 border-t border-gray-800" data-testid="contact-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.title}</h2>
        </div>

        {success && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
            role="status"
            aria-live="polite"
            data-testid="success-message"
          >
            <div className="bg-green-500/95 text-white px-8 py-6 rounded-xl shadow-2xl max-w-sm w-full text-center">
              <Check size={48} className="mx-auto mb-3" />
              <p className="text-lg font-semibold">
                Your inquiry has been received!
              </p>
              <p className="text-sm mt-1 text-green-50/90">
                We will contact you soon.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg" data-testid="error-message">
            {error}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          noValidate
          className="bg-gray-900 border border-[#FFDD59] rounded-2xl shadow-xl p-8 space-y-6"
        >
          {/* Screen-reader announcement (WCAG 2.1 AA) */}
          <div
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0, 0, 0, 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            {formErrors.length > 0
              ? `Form has ${formErrors.length} error${formErrors.length > 1 ? 's' : ''}. ${formErrors[0].message}`
              : ''}
          </div>

          {/* Visible error banner */}
          {formErrors.length > 0 && (
            <div className="bg-red-900/20 border border-red-500/40 rounded p-4 text-red-300" data-testid="form-errors-banner">
              <p className="font-semibold mb-2">
                Please fix the following {formErrors.length === 1 ? 'error' : 'errors'}:
              </p>
              <ul className="list-disc list-inside text-sm space-y-1">
                {formErrors.map((err, idx) => (
                  <li key={idx}>{err.message}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t.name} <span className="text-red-500">*</span>
              </label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="input-name"
              />
              {errorFor('name') && (
                <p className={fieldErrorCls}>{errorFor('name')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t.company} <span className="text-red-500">*</span>
              </label>
              <input
                id="company"
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="input-company"
              />
              {errorFor('company') && (
                <p className={fieldErrorCls}>{errorFor('company')}</p>
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t.email} <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="input-email"
              />
              {errorFor('email') && (
                <p className={fieldErrorCls}>{errorFor('email')}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t.phone} <span className="text-red-500">*</span>
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handlePhoneChange}
                placeholder="+971 50 123 4567"
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="input-phone"
              />
              <p className="text-xs text-gray-400 mt-1">
                Include your country code (e.g. +971 for UAE).
              </p>
              {errorFor('phone') && (
                <p className={fieldErrorCls}>{errorFor('phone')}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              {t.message} <span className="text-red-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows="6"
              placeholder={t.messagePlaceholder}
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
              data-testid="input-message"
            />
            {errorFor('message') && (
              <p className={fieldErrorCls}>{errorFor('message')}</p>
            )}
          </div>

          <div className="bg-yellow-50/10 border border-[#FFDD59]/40 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer mb-3">
              <input
                id="notice_acknowledged"
                type="checkbox"
                checked={noticeAcknowledged}
                onChange={(e) => setNoticeAcknowledged(e.target.checked)}
                aria-required="true"
                autoComplete="off"
                className="mt-0.5 w-4 h-4 accent-[#FFDD59]"
                data-testid="checkbox-notice-acknowledged"
              />
              <span className="text-sm text-gray-300">
                I have read and understood the{' '}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#FFDD59] underline hover:text-yellow-300"
                >
                  Persona Privacy Policy
                </a>
                . <span className="text-red-500">*</span>
              </span>
            </label>
            {errorFor('notice_acknowledged') && (
              <p className={fieldErrorCls + ' ml-7'}>{errorFor('notice_acknowledged')}</p>
            )}

            <label className="flex items-start gap-3 cursor-pointer mt-3">
              <input
                id="explicit_consent"
                type="checkbox"
                checked={explicitConsent}
                onChange={(e) => setExplicitConsent(e.target.checked)}
                aria-required="true"
                autoComplete="off"
                className="mt-0.5 w-4 h-4 accent-[#FFDD59]"
                data-testid="checkbox-explicit-consent"
              />
              <span className="text-sm text-gray-300">
                I give my explicit consent for the processing of my personal data for the
                purpose of brand-influencer collaboration inquiries.{' '}
                <span className="text-red-500">*</span>
              </span>
            </label>
            {errorFor('explicit_consent') && (
              <p className={fieldErrorCls + ' ml-7'}>{errorFor('explicit_consent')}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-400 transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="submit-button"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={20} />
                {t.submitting}
              </>
            ) : (
              <>
                <Send size={20} />
                {t.submit}
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default BrandContact;
